import Layout from 'components/layout/layout'
import { Route, Routes } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import RestrictionWrapper, { EDITOR_ROLES, OWNER_ROLES, WORKER_ROLES } from 'features/auth/restrictionWrapper'
import AccountCompletion from 'features/auth/accountCompletion'
import { AuthenticationStatus } from 'types'
import NotFoundPage from './pages/NotFoundPage'
import ForbiddenPage from './pages/ForbiddenPage'
import HomePage from './pages/HomePage'
import PricingPage from './pages/PricingPage'
import SignUpPage from './pages/SignUpPage'
import LoginPage from './pages/LoginPage'
import LostPasswordPage from './pages/LostPasswordPage'
import ClaimAccountPage from './pages/ClaimAccountPage'
import AccountPage from './pages/account/AccountPage'
import CompleteAccountPage from './pages/account/CompleteAccountPage'
import AccessInvitationsPage from './pages/account/AccessInvitationsPage'
import NotificationsPage from './pages/account/NotificationsPage'
import SecuritySettingsPage from './pages/account/SecuritySettingsPage'
import AvatarSettingsPage from './pages/account/AvatarSettingsPage'
import StoragesPage from './pages/storages/StoragesPage'
import Storagepage from './pages/storages/StoragePage'
import EditStoragePage from './pages/storages/EditStoragePage'
import DeleteStoragePage from './pages/storages/DeleteStoragePage'
import CreateStoragePage from './pages/storages/CreateStoragePage'
import StorageAccessPage from './pages/storages/StorageAccessPage'
import StorageStatisticsPage from './pages/storages/StorageStatisticsPage'
import HistoryPage from './pages/storages/HistoryPage'
import LegacyStorageRedirect from './features/navigation/LegacyStorageRedirect'
import CategoriesPage from './pages/storages/categories/CategoriesPage'
import CreateCategoryPage from './pages/storages/categories/CreateCategoryPage'
import CategoryPage from './pages/storages/categories/CategoryPage'
import EditCategoryPage from './pages/storages/categories/EditCategoryPage'
import CustomInputsEditPage from './pages/storages/categories/custom_inputs/CustomInputsEditPage'
import LocationsPage from './pages/storages/locations/LocationsPage'
import CreateLocationPage from './pages/storages/locations/CreateLocationPage'
import LocationPage from './pages/storages/locations/LocationPage'
import EditLocationPage from './pages/storages/locations/EditLocationPage'
import CustomersPage from './pages/storages/customers/CustomersPage'
import CreateCustomerPage from './pages/storages/customers/CreateCustomerPage'
import CustomerPage from './pages/storages/customers/CustomerPage'
import EditCustomerPage from './pages/storages/customers/EditCustomerPage'
import SuppliersPage from './pages/storages/suppliers/SuppliersPage'
import CreateSupplierPage from './pages/storages/suppliers/CreateSupplierPage'
import SupplierPage from './pages/storages/suppliers/SupplierPage'
import EditSupplierPage from './pages/storages/suppliers/EditSupplierPage'
import TransactionsPage from './pages/storages/transactions/TransactionsPage'
import CreateTransactionPage from './pages/storages/transactions/CreateTransactionPage'
import EditTransactionPage from './pages/storages/transactions/EditTransactionPage'
import TransactionPage from './pages/storages/transactions/TransactionPage'
import useAccess from './hooks/useAccess'
import ProductsPage from './pages/storages/products/ProductsPage'
import CreateProductPage from './pages/storages/products/CreateProductPage'
import ProductPage from './pages/storages/products/ProductPage'
import EditProductPage from './pages/storages/products/EditProductPage'
import GeneralSettingsPage from './pages/account/GeneralSettingsPage'
import DeleteAccountPage from './pages/account/DeleteAccountPage'
import LegalPage from './pages/legal/LegalPage'
import FaqPage from './pages/FaqPage'
import SupportPage from './pages/SupportPage'
import TutorialsPage from './pages/tutorials/TutorialsPage'
import SearchTutorialPage from './pages/tutorials/SearchTutorialPage'
import useAnalyticsPageViews from './hooks/useAnalyticsPageViews'

function App() {

	const { i18n } = useTranslation()

    useAccess({})
    useAnalyticsPageViews()

    useEffect(() => {
        const lang = localStorage.getItem('lang')
        if (lang) {
            i18n.changeLanguage(lang)
        }
    }, [i18n])

    return (
        <div className="App">
            <Routes>
                    <Route element={<Layout />}>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        <Route path="/faq" element={<FaqPage />} />
                        <Route path="/support" element={<SupportPage />} />
                        <Route path="/tutorials" element={<TutorialsPage />} />
                        <Route path="/tutorials/search" element={<SearchTutorialPage />} />
                        <Route path="/privacy" element={<LegalPage doc="privacy" />} />
                        <Route path="/terms" element={<LegalPage doc="terms" />} />
                        <Route path="/sign-up" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.offline} fallbackURL='/storages'>
                                <SignUpPage />
                            </RestrictionWrapper>
                        } />
                        <Route path="/login" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.offline} fallbackURL='/storages'>
                                <LoginPage />
                            </RestrictionWrapper>
                        } />
                        <Route path="/lost-password" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.offline} fallbackURL='/storages'>
                                <LostPasswordPage />
                            </RestrictionWrapper>
                        } />
                        <Route path="/claim-account/:token" element={<ClaimAccountPage />} />

                        <Route path="/account" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <AccountPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/complete-account" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <CompleteAccountPage />
                            </RestrictionWrapper>
                        } />

                        <Route path="/access-invitations" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <AccessInvitationsPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                            <Route path="/notifications" element={
                                <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                    <NotificationsPage />
                                </RestrictionWrapper>
                            } />

                        <Route path="/account/security-settings" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <SecuritySettingsPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />

                        <Route path="/account/general-settings" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <GeneralSettingsPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />

                        <Route path="/account/avatar-settings" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <AvatarSettingsPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />

                        <Route path="/account/delete" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <DeleteAccountPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />

                        <Route path="/storages" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <StoragesPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <Storagepage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/edit" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={OWNER_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <EditStoragePage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/delete" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={OWNER_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <DeleteStoragePage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storages/create" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <CreateStoragePage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storages/:storageId/*" element={<LegacyStorageRedirect />} />

                        <Route path="/storage/access" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={OWNER_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <StorageAccessPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/statistics" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <StorageStatisticsPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/history" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <HistoryPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/categories" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <CategoriesPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/categories/create" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={EDITOR_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <CreateCategoryPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/categories/:categoryId" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <CategoryPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/categories/:categoryId/edit" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={EDITOR_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <EditCategoryPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/categories/:categoryId/edit_custom_inputs" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={EDITOR_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <CustomInputsEditPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />

                        <Route path="/storage/locations" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <LocationsPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/locations/create" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={EDITOR_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <CreateLocationPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/locations/:locationId" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <LocationPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/locations/:locationId/edit" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={EDITOR_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <EditLocationPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />

                        <Route path="/storage/products" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <ProductsPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/products/create" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={EDITOR_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <CreateProductPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/products/:productId" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <ProductPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/products/:productId/edit" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={EDITOR_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <EditProductPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />

                        <Route path="/storage/customers" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <CustomersPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/customers/create" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={EDITOR_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <CreateCustomerPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/customers/:customerId" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <CustomerPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/customers/:customerId/edit" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={EDITOR_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <EditCustomerPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/suppliers" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <SuppliersPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/suppliers/create" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={EDITOR_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <CreateSupplierPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/suppliers/:supplierId" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <SupplierPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/suppliers/:supplierId/edit" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={EDITOR_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <EditSupplierPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/transactions" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <TransactionsPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/transactions/create/:transactionType" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={WORKER_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <CreateTransactionPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/transactions/:transactionId/edit" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} requiredRole={WORKER_ROLES} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <EditTransactionPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/storage/transactions/:transactionId" element={
                            <RestrictionWrapper restrictedTo={AuthenticationStatus.online} fallbackURL='/login'>
                                <AccountCompletion fallbackURL='/complete-account'>
                                    <TransactionPage />
                                </AccountCompletion>
                            </RestrictionWrapper>
                        } />
                        <Route path="/401" element={<ForbiddenPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                    </Route>
                </Routes>
        </div>
    )
}

export default App